import { ClientTransferRequestId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { Checkbox } from "components/ui/checkbox";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { ClientTransferSubject } from "./transfer-types";

export interface IncomingTransfer {
    id: ClientTransferRequestId;
    kind: ClientTransferSubject["kind"];
    includeNotes: boolean;
    includeFiles: boolean;
}

interface Props {
    transfer: IncomingTransfer;
    onTransferChanged: () => void;
}

const shareLabels: Record<ClientTransferSubject["kind"], string> = {
    placement: "Share status and move dates with the sender",
    homeCareLead: "Share status, care dates, and hours per week with the sender",
};

export const IncomingTransferActions: React.FC<Props> = props => {
    const { transfer, onTransferChanged } = props;
    const regionId = useRegionId();
    const trpcContext = trpc.useTRPC();
    const [shareStatusWithSender, setShareStatusWithSender] = React.useState(true);

    const acceptTransfer = useMutation(
        trpcContext.clientTransfer.acceptRequest.mutationOptions(),
    );
    const rejectTransfer = useMutation(
        trpcContext.clientTransfer.reject.mutationOptions(),
    );
    const busy = acceptTransfer.isPending || rejectTransfer.isPending;

    const onAcceptTransfer = React.useCallback(async () => {
        if (acceptTransfer.isPending) {
            return;
        }
        try {
            const accepted = await acceptTransfer.mutateAsync({
                clientTransferRequestId: transfer.id,
                regionId: regionId,
                shareStatusWithSender,
            });
            handleEvent("client-transfer-accept", {
                subjectKind: transfer.kind,
                includeNotes: transfer.includeNotes,
                includeFiles: transfer.includeFiles,
                shareStatusWithSender,
            });
            navigate(accepted.appLink);
        } catch {
            await fancyAlert(
                "Unable to accept transfer",
                "This transfer request may have already been accepted, declined, or cancelled.",
            );
            onTransferChanged();
        }
    }, [acceptTransfer, transfer, regionId, shareStatusWithSender, onTransferChanged]);

    const onRejectTransfer = React.useCallback(async () => {
        const confirm = await fancyConfirm(
            "Decline transfer?",
            "This will decline the transfer request. The client will not be transferred.",
            "Yes, decline",
            "No, go back",
        );

        if (!confirm) {
            return;
        }
        try {
            await rejectTransfer.mutateAsync({
                clientTransferRequestId: transfer.id,
            });
        } catch {
            await fancyAlert(
                "Unable to decline transfer",
                "This transfer request may have already been accepted, declined, or cancelled.",
            );
        }
        onTransferChanged();
    }, [rejectTransfer, transfer.id, onTransferChanged]);

    return (
        <div className="flex flex-wrap items-center justify-between gap-3">
            <Checkbox
                className="text-sm text-gray-700 md:w-auto"
                label={shareLabels[transfer.kind]}
                checked={shareStatusWithSender}
                disabled={busy}
                onChange={e => setShareStatusWithSender(e.target.checked)}
            />
            <div className="flex flex-wrap items-center justify-end gap-1.5">
                {busy ? <Spinner /> : null}
                <PrimaryCompactButton
                    type="button"
                    onClick={onAcceptTransfer}
                    disabled={busy}>
                    Accept
                </PrimaryCompactButton>
                <SecondaryCompactButton
                    type="button"
                    onClick={onRejectTransfer}
                    disabled={busy}>
                    Decline
                </SecondaryCompactButton>
            </div>
        </div>
    );
};
